import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GetDatesRequestInfo, GoalsSummaryData } from '@app/client/pg-goals/pg-goals.utilities';
import { mapMetricsToGoalType, ScenariosType, ViewCharts } from '@app/client/scenarios/scenarios.model';
import { OptimizationTypes } from '@app/configurations';
import { microsToNumber } from '@app/core/utilities/number.utilities';
import { environment } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GoalMetrics } from '../models/common.models';
import { Currency } from '../models/customer.models';
import {
  CLICKS,
  CONVERSIONS,
  COST,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_REVENUE_RATIO,
  currencyGoals,
  MIN_DEFAULT_COST_DAY_GOAL_VALUE,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PROFIT_GROSS,
  PROFIT_NET,
  RETURN_ON_INVESTMENT,
  REVENUE,
  ROAS,
} from '../models/metrics.constants';
import { LoggingService } from '../services/error-logging.service';
import { deepClone } from '../utilities/object.utilities';

export function ScenariosOptions(type) {
  const view: ViewCharts = {
    CLICK: {
      chart: {
        menu: {
          defaultOptions: [COST_PER_CLICK, CLICKS],
          selected: [],
          xAxisMenu: [COST_PER_CLICK, COST, CLICKS],
          yAxisMenu: [COST_PER_CLICK, COST, CLICKS],
        },
      },
      table: {
        menu: [COST_PER_CLICK, COST, CLICKS],
      },
    },
    REVENUE: {
      chart: {
        menu: {
          defaultOptions: [COST, REVENUE],
          selected: [],
          xAxisMenu: [COST, COST_REVENUE_RATIO, ROAS, COST_PER_CONVERSION],
          yAxisMenu: [
            COST_PER_CONVERSION,
            CLICKS,
            COST_PER_CLICK,
            CONVERSIONS,
            COST,
            COST_REVENUE_RATIO,
            ROAS,
            REVENUE,
          ],
        },
      },
      table: {
        menu: [COST_PER_CONVERSION, CLICKS, COST_PER_CLICK, CONVERSIONS, COST, COST_REVENUE_RATIO, ROAS, REVENUE],
      },
    },
    VCPM: {
      chart: {
        menu: {
          defaultOptions: [COST, REVENUE],
          selected: [],
          xAxisMenu: [COST, COST_REVENUE_RATIO, ROAS, COST_PER_CONVERSION],
          yAxisMenu: [
            COST_PER_CONVERSION,
            CLICKS,
            COST_PER_CLICK,
            CONVERSIONS,
            COST,
            COST_REVENUE_RATIO,
            ROAS,
            REVENUE,
          ],
        },
      },
      table: {
        menu: [COST_PER_CONVERSION, CLICKS, COST_PER_CLICK, CONVERSIONS, COST, COST_REVENUE_RATIO, ROAS, REVENUE],
      },
    },
    PROFIT: {
      chart: {
        menu: {
          defaultOptions: [COST_PER_CONVERSION, PROFIT_NET],
          selected: [],
          xAxisMenu: [COST, RETURN_ON_INVESTMENT, COST_PER_CONVERSION],
          yAxisMenu: [COST_PER_CONVERSION, CLICKS, COST_PER_CLICK, CONVERSIONS, COST, RETURN_ON_INVESTMENT, PROFIT_NET],
        },
      },
      table: {
        menu: [COST_PER_CONVERSION, CLICKS, COST_PER_CLICK, CONVERSIONS, COST, RETURN_ON_INVESTMENT, PROFIT_NET],
      },
    },
  };

  return view[type];
}

export function ScenariosChartOptions(type: ScenariosType, info, { xAxisMenu, yAxisMenu, defaultOptions }) {
  const xAxis: any = {};
  const yAxis: any = {};
  xAxis.options = xAxisMenu;
  yAxis.options = yAxisMenu;

  if (type === ScenariosType.SCENARIOS) {
    const goal = mapMetricsToGoalType[info.goal];
    xAxis.selected = xAxisMenu.includes(goal) ? goal : defaultOptions[0];
    yAxis.selected = defaultOptions[1];

    if (xAxis.selected === yAxis.selected) yAxis.selected = yAxisMenu.find((item) => item !== xAxis.selected);
  } else {
    if (info.id && info.goal) {
      xAxis.selected = info.goal !== PG_GOAL_METRIC_PROFIT_MAXIMIZATION ? mapMetricsToGoalType[info.goal] : PROFIT_NET;
    } else {
      if (info.realType === OptimizationTypes.PROFIT) {
        xAxis.selected = COST;
        yAxis.selected = PROFIT_NET;
      } else if (info.realType === OptimizationTypes.REVENUE) {
        xAxis.selected = COST;
        yAxis.selected = REVENUE;
      } else {
        xAxis.selected = COST_PER_CLICK;
        yAxis.selected = CLICKS;
      }
    }
    // if selected value is not in options update to first item in options
    xAxis.selected = xAxis.options.includes(xAxis.selected) ? xAxis.selected : xAxis.options[0];
    yAxis.selected = defaultOptions[1];

    if (xAxis.selected === yAxis.selected) yAxis.selected = yAxisMenu.find((item) => item !== xAxis.selected);
  }

  return { xAxis, yAxis };
}

export function FilterScenariosData(data, xAxis, yAxis) {
  let tmp = deepClone(data);
  // sort ascending by x-axis. Filter y-values below 0
  tmp = tmp.sort((a, b) => a[xAxis] - b[xAxis]).filter((a) => a[yAxis] >= 0);

  // get point diff between current and previous y value
  const deltas = tmp.map((a, i) => (i ? Math.abs(tmp[i - 1][yAxis] - a[yAxis]) : -1));
  const maxDelta = Math.max(...deltas);
  const threshold = 0.05;

  const filterThreshold = (data) => {
    let overThreshold = false;

    return data.filter((_, i) => {
      if (overThreshold) return true;

      // check if delta (or prev/next if first) is above threshold
      return (overThreshold = maxDelta * threshold < (deltas[i] === -1 ? deltas[i - 1] || deltas[i + 1] : deltas[i]));
    });
  };

  // ascending
  tmp = filterThreshold(tmp);
  tmp.reverse();
  deltas.reverse();

  // descending
  tmp = filterThreshold(tmp);
  tmp.reverse();

  // set correct id
  return tmp.map((a, id) => ({ ...a, id }));
}

@Injectable()
export class ScenariosAPI {
  constructor(
    private http: HttpClient,
    private logger: LoggingService,
  ) {}
  getScenarios(
    customerId: number,
    performance_group_id = null,
    currency: Currency,
    campaigns = [],
    goal,
  ): Observable<any> {
    let payload: any = {
      currency,
      limit: 100,
    };
    if (performance_group_id) payload.performance_group_id = performance_group_id;

    if (campaigns?.length) payload.adspert_campaign_ids = campaigns;

    if (goal) payload = { ...payload, ...goal };

    if (currencyGoals.includes(payload?.goal_type) && payload?.goal_value < MIN_DEFAULT_COST_DAY_GOAL_VALUE)
      payload.goal_value = MIN_DEFAULT_COST_DAY_GOAL_VALUE;

    if (payload?.goal_type === PG_GOAL_METRIC_PROFIT_MAXIMIZATION) payload.goal_value = null;

    return this.http.post(`${environment.app.resources.dashboard}customers/${customerId}/scenarios`, payload, {}).pipe(
      map((res: any) => {
        return res.data.map((d, i) => {
          d.cost_per_conversion = microsToNumber(d.cost_per_conversion);
          d.cost_per_click = microsToNumber(d.cost_per_click);
          d.cost = microsToNumber(d.cost);
          d.revenue = microsToNumber(d.revenue);
          if (d.profit_gross !== null) d.profit_gross = microsToNumber(d.profit_gross);
          if (d.profit_net !== null) d.profit_net = microsToNumber(d.profit_net);
          // use net profit for PROFIT if opti type is PROFIT, for other cases we dont need profit
          d.id = i;
          return d;
        });
      }),
      catchError((error: any) => {
        this.logger.logError('Could not fetch scenarios', error, 'error');
        return throwError('Could not fetch scenarios');
      }),
    );
  }

  getHistoricValues(
    customerId,
    currency,
    optiType,
    campaigns = [],
    performanceGroupId = null,
  ): Observable<GoalMetrics> {
    const payload: Record<string, any> = { ...GetDatesRequestInfo(), currency };
    if (campaigns.length) payload['filter_adspert_campaign_id'] = campaigns;
    else if (!!performanceGroupId) payload['filter_performance_group_id'] = [performanceGroupId];

    return this.http
      .post(`${environment.app.resources.dashboard}customers/${customerId}/performance`, payload, {})
      .pipe(
        map((res: any) => GoalsSummaryData(res.data, PROFIT_GROSS, payload.limit)),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch pg metrics', error, 'error');
          return throwError('Failed to fetch pg metrics');
        }),
      );
  }
}
